import React from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AndroidVost from '../../image/android_vost.jpg';
import { AuthHelpSameAccountItemDiv } from './AuthHelpSameAccountItemDiv';
import { SetupsItemUstroistvDiv } from './SetupsItemUstroistvDiv';
import {
  AuthHelpRestoreLayout,
  InstructionImage,
  InstructionSetup,
  InstructionSteps,
  StageHeader,
} from './AuthHelpRestoreLayout';

interface Props {
  authType: 'google' | 'huawei';
  sameAccount: boolean;
  showSetup: boolean;
}

const stages = [
  'Подготовьте вход через Яндекс ID или ВК ID',
  'Восстановите подписку',
  'Войдите на втором устройстве',
];

export const AuthHelpGoogleHuaweiRestoreItemDiv = ({
  authType,
  sameAccount,
  showSetup,
}: Props) => {
  const provider = authType === 'huawei' ? 'Huawei' : 'Google';

  if (sameAccount) {
    return (
      <AuthHelpSameAccountItemDiv
        provider={`${provider} ID`}
        idPrefix="google-huawei-restore-stage"
      />
    );
  }

  return (
    <AuthHelpRestoreLayout
      stages={stages}
      idPrefix="google-huawei-restore-stage"
      intro="Первые два этапа выполните на устройстве, где уже есть подписка."
    >
      <Stack
        component="section"
        id="google-huawei-restore-stage-1"
        spacing={2.5}
        sx={{ scrollMarginTop: 24 }}
      >
        <StageHeader
          stage={1}
          totalStages={stages.length}
          title={stages[0]}
          secondDevice={false}
        />
        <SetupsItemUstroistvDiv fixedPlatform="android" device="current" />
      </Stack>
      <Divider />
      <Stack
        component="section"
        id="google-huawei-restore-stage-2"
        spacing={2.5}
        sx={{ scrollMarginTop: 24 }}
      >
        <StageHeader
          stage={2}
          totalStages={stages.length}
          title={stages[1]}
          secondDevice={false}
        />
        <InstructionSteps
          steps={[
            {
              id: 'open-app',
              text: (
                <>
                  Откройте приложение <strong>Учи Мед</strong>.
                </>
              ),
            },
            {
              id: 'profile',
              text: 'Откройте свой профиль в правом верхнем углу.',
            },
            {
              id: 'sign-out',
              text: (
                <>
                  Нажмите на три точки рядом с именем и выберите{' '}
                  <strong>«Выйти из профиля»</strong>.
                </>
              ),
            },
            {
              id: 'provider-sign-in',
              important: true,
              text: (
                <>
                  Нажмите именно <strong>«Войти с {provider}»</strong>.
                </>
              ),
            },
            {
              id: 'choose-id',
              text: (
                <>
                  В окне <strong>«Вход через {provider} ID недоступен»</strong>{' '}
                  выберите вход через <strong>Яндекс ID</strong> или{' '}
                  <strong>ВК ID</strong>.
                </>
              ),
            },
            {
              id: 'link-account',
              text: (
                <>
                  После авторизации на экране{' '}
                  <strong>«Привязать старый аккаунт»</strong> нажмите{' '}
                  <strong>«Войти с {provider}»</strong>.
                </>
              ),
            },
            {
              id: 'subscription-account',
              text: (
                <>
                  Выберите <strong>тот {provider} ID</strong>, на котором была
                  подписка.
                </>
              ),
            },
            {
              id: 'check-subscriptions',
              text: (
                <>
                  После входа нажмите <strong>«Продолжить»</strong> и проверьте
                  вкладку <strong>«Мои подписки»</strong>.
                </>
              ),
            },
          ]}
        />
        <InstructionImage
          image={AndroidVost}
          label="Увеличить схему восстановления подписки"
          aspectRatio="1280 / 356"
        />
      </Stack>

      <Divider />

      <Stack
        component="section"
        id="google-huawei-restore-stage-3"
        spacing={2.5}
        sx={{ scrollMarginTop: 24 }}
      >
        <StageHeader
          stage={3}
          totalStages={stages.length}
          title={stages[2]}
          secondDevice
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ p: 1.5, bgcolor: '#eaf2f5', borderLeft: '3px solid #4f8298' }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 20, color: '#3c6b7e', flexShrink: 0, mt: 0.25 }}
          />
          <Typography component="p" fontSize="13px" lineHeight={1.7}>
            Если вы уже входили на втором устройстве,{' '}
            <strong>выйдите из профиля и войдите заново</strong> после
            восстановления подписки.
          </Typography>
        </Stack>
        {showSetup && <InstructionSetup />}
        <InstructionSteps
          steps={[
            {
              id: 'second-device',
              text: (
                <>
                  Откройте <strong>Учи Мед</strong> на втором устройстве.
                </>
              ),
            },
            {
              id: 'same-account',
              text: (
                <>
                  Войдите через <strong>тот же Яндекс ID или ВК ID</strong>,
                  который выбрали на этапе 2.
                </>
              ),
            },
            {
              id: 'second-subscriptions',
              text: (
                <>
                  Проверьте вкладку <strong>«Мои подписки»</strong>.
                </>
              ),
            },
          ]}
        />
      </Stack>
    </AuthHelpRestoreLayout>
  );
};
